/**
 * Order fulfilment status vocabulary shared by the calling board (customer
 * display) and the calling page (staff).
 *
 * These values live on the existing `status` field of an online order. The iPad
 * POS already drives that field, so the board reads what the POS writes and the
 * staff page writes what the POS reads - there is no second source of truth.
 *
 * Lifecycle:
 *   paying -> received -> paid | accepted | clientAccepted -> sent -> readyForPickup -> collected
 *             \________________ preparing ________________________/   \__ ready __/   \_ off _/
 *
 * Every in-progress status has to be listed, or an order silently drops off the
 * board mid-service. When the iPad accepts an order it writes
 * MobileOrder.finalStatusAfterLocalSave - "paid" once payment has settled (which
 * a kiosk card order always has) and "accepted" otherwise - while a counter
 * client writes "clientAccepted".
 */

import { isDelivery, isPickupOrDelivery } from "util/restaurantUtils";
import { transformOrderId } from "util/orderUtils";

export const ORDER_STATUS_RECEIVED = "received";
export const ORDER_STATUS_PAID = "paid";
export const ORDER_STATUS_ACCEPTED = "accepted";
export const ORDER_STATUS_CLIENT_ACCEPTED = "clientAccepted";
export const ORDER_STATUS_SENT = "sent";
export const ORDER_STATUS_READY_FOR_PICKUP = "readyForPickup";
export const ORDER_STATUS_COLLECTED = "collected";

export const BUCKET_PREPARING = "preparing";
export const BUCKET_READY = "ready";

const PREPARING_STATUSES = [
  ORDER_STATUS_RECEIVED,
  ORDER_STATUS_PAID,
  ORDER_STATUS_ACCEPTED,
  ORDER_STATUS_CLIENT_ACCEPTED,
  ORDER_STATUS_SENT,
];

const READY_STATUSES = [ORDER_STATUS_READY_FOR_PICKUP];

// Mirrors the server-side board TTLs so both screens drop an entry at the same
// moment; see functions/APIs/order/callingBoardUtils.js.
export const READY_TTL_MINUTES = 15;
export const PREPARING_TTL_MINUTES = 120;

const MINUTE_MS = 60 * 1000;

/**
 * Order timestamps arrive as a Firestore Timestamp serialised to JSON, a plain
 * ISO string, or a raw epoch, depending on which writer produced them.
 */
export const toMillis = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "number") {
    return value;
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value._seconds === "number") {
    return value._seconds * 1000;
  }
  if (typeof value.seconds === "number") {
    return value.seconds * 1000;
  }
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

export const KIND_PAGER = "pager";
export const KIND_ORDER = "order";

/**
 * The pager number handed to the customer at the kiosk, or null when they were
 * not given one.
 *
 * The kiosk writes it to `order.pagerNumber` and the server duplicates it onto
 * the document root; both are read, and a blank counts as "no pager". Mirrors
 * MobileOrder.resolvedPagerNumber in the iPad POS and the kiosk's own order
 * success screen, so every surface calls the same number.
 */
export const resolvePagerNumber = (order) => {
  if (!order) {
    return null;
  }
  const candidates = [order.pagerNumber, order.order && order.order.pagerNumber];
  for (const candidate of candidates) {
    if (typeof candidate === "string" || typeof candidate === "number") {
      const trimmed = String(candidate).trim();
      if (trimmed !== "") {
        return trimmed;
      }
    }
  }
  return null;
};

/**
 * The number to call for an order: the customer's pager number when they were
 * given one, otherwise the order reference printed on their receipt.
 */
export const callingNumber = (order) => {
  const pagerNumber = resolvePagerNumber(order);
  if (pagerNumber) {
    return { ref: pagerNumber, kind: KIND_PAGER };
  }
  return { ref: transformOrderId(order && order.id), kind: KIND_ORDER };
};

export const boardBucket = (status) => {
  if (READY_STATUSES.includes(status)) {
    return BUCKET_READY;
  }
  if (PREPARING_STATUSES.includes(status)) {
    return BUCKET_PREPARING;
  }
  return null;
};

/** Delivery orders are collected by a driver, so they never go on the board. */
export const isDeliveryOrder = (order) => {
  if (!order) {
    return false;
  }
  const orderType = order.order && order.order.type;
  if (isDelivery(orderType) || isPickupOrDelivery(orderType)) {
    return true;
  }
  return Boolean(order.deliveryPartner || order.deliveryQuoteId);
};

/** Age is measured from the last status change, falling back to order time. */
export const orderTimestampMs = (order) => {
  const statusMs = toMillis(order && order.statusUpdatedAt);
  return statusMs !== null ? statusMs : toMillis(order && order.createdAt);
};

// The order types whose customer is standing in the shop waiting to be called.
// Anything else in `order.type` is a table id, i.e. dine-in.
const COLLECTION_ORDER_TYPES = ["takeaway", "pickup", "foodcourt"];

/**
 * A dine-in order is carried to the table, so its customer is never called.
 * It matters more than it looks: the POS marks an order "paid" when the bill is
 * settled, so without this every table that pays would appear on the board.
 *
 * An order with no type is not assumed to be dine-in - better a stray row than
 * a customer who is never called.
 */
export const isDineInOrder = (order) => {
  const orderType = order && order.order && order.order.type;
  if (!orderType) {
    return false;
  }
  if (COLLECTION_ORDER_TYPES.includes(orderType)) {
    return false;
  }
  return !isDelivery(orderType) && !isPickupOrDelivery(orderType);
};

export const isCallable = (order) => {
  if (!order || order.permanentDeletion === true || order.isDeleted === true) {
    return false;
  }
  if (isDeliveryOrder(order) || isDineInOrder(order)) {
    return false;
  }
  return boardBucket(order.status) !== null;
};

export const isExpired = (order, now = Date.now(), ttls = {}) => {
  const {
    readyTtlMinutes = READY_TTL_MINUTES,
    preparingTtlMinutes = PREPARING_TTL_MINUTES,
  } = ttls;

  const bucket = boardBucket(order && order.status);
  const ttlMinutes =
    bucket === BUCKET_READY
      ? readyTtlMinutes
      : bucket === BUCKET_PREPARING
        ? preparingTtlMinutes
        : 0;

  if (!ttlMinutes) {
    return false;
  }

  const timestampMs = orderTimestampMs(order);
  if (timestampMs === null) {
    // No usable timestamp: keep it rather than hide an order a customer waits for.
    return false;
  }
  return now - timestampMs > ttlMinutes * MINUTE_MS;
};

/** Ready first, then longest-waiting first - the order staff should act on. */
export const compareForCalling = (a, b) => {
  const bucketA = boardBucket(a && a.status);
  const bucketB = boardBucket(b && b.status);
  if (bucketA !== bucketB) {
    return bucketA === BUCKET_READY ? -1 : 1;
  }
  return (orderTimestampMs(a) || 0) - (orderTimestampMs(b) || 0);
};

/** The live working set for the staff calling page. */
export const selectCallableOrders = (orders, now = Date.now(), ttls = {}) =>
  (orders || [])
    .filter((order) => isCallable(order))
    .filter((order) => !isExpired(order, now, ttls))
    .sort(compareForCalling);

/**
 * The single action a staff member can take on an order, or null when there is
 * nothing to do. Deliberately one button: two taps move an order all the way
 * from the kitchen to collected.
 */
export const nextAction = (order) => {
  const bucket = boardBucket(order && order.status);
  if (bucket === BUCKET_PREPARING) {
    return { status: ORDER_STATUS_READY_FOR_PICKUP, label: "Mark ready" };
  }
  if (bucket === BUCKET_READY) {
    return { status: ORDER_STATUS_COLLECTED, label: "Collected" };
  }
  return null;
};

/** How long an order has been waiting, for the "waiting 4 min" hint. */
export const waitingMinutes = (order, now = Date.now()) => {
  const timestampMs = orderTimestampMs(order);
  if (timestampMs === null || now < timestampMs) {
    return 0;
  }
  return Math.floor((now - timestampMs) / MINUTE_MS);
};

/**
 * The Firestore day partition an order lives in, as `updateOrderStatus` expects
 * it (`yearStr` = YYYY, `dateStr` = DD-MM-YY).
 *
 * `getOrders` returns these on each order; that is the value to trust, because
 * the server derives them in the restaurant's own timezone. The `createdAt`
 * fallback exists only so the page still works against a server that predates
 * that change - it uses the browser clock and so can be a day out for a
 * restaurant in another timezone.
 */
export const orderPartition = (order) => {
  if (!order) {
    return null;
  }
  if (order.yearStr && order.dateStr) {
    return { yearString: order.yearStr, dateString: order.dateStr };
  }

  const createdMs = toMillis(order.createdAt);
  if (createdMs === null) {
    return null;
  }

  const created = new Date(createdMs);
  const pad = (value) => String(value).padStart(2, "0");
  return {
    yearString: String(created.getFullYear()),
    dateString: `${pad(created.getDate())}-${pad(created.getMonth() + 1)}-${String(
      created.getFullYear()
    ).slice(-2)}`,
  };
};

/**
 * Orders live in per-day Firestore partitions, so just after midnight the ones
 * still cooking sit in yesterday's. Mirrors PREVIOUS_DAY_CUTOFF_HOUR on the
 * server (functions/APIs/order/callingBoard.js) so the staff page and the
 * customer board never disagree about which orders are live.
 */
export const CALLING_PREVIOUS_DAY_CUTOFF_HOUR = 6;

export const callingQueryRange = (now = new Date()) => {
  const pad = (value) => String(value).padStart(2, "0");
  const asDateStr = (date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

  const toDate = asDateStr(now);
  if (now.getHours() >= CALLING_PREVIOUS_DAY_CUTOFF_HOUR) {
    return { fromDate: toDate, toDate };
  }

  const yesterday = new Date(now.getTime());
  yesterday.setDate(yesterday.getDate() - 1);
  return { fromDate: asDateStr(yesterday), toDate };
};

/**
 * Grid sizing for a calling board column, plus the ceiling on the number inside.
 *
 * A wall display should spend its whole height on the numbers. The lever is the
 * tile: the number is sized against its own tile's width, so a wider tile is a
 * bigger number, and a quiet board therefore wants few wide tiles while a busy
 * one wants many narrow ones.
 *
 * The two ceilings are the other half. Width alone would let a one-glyph pager
 * number tower over a four-glyph order reference and push the rows so tall that
 * the bottom of the queue fell off the screen, so each tier also caps the number
 * at the share of screen height that lets a full column stay visible. A pager
 * tile carries a "Pager" line above its number and so spends more of the row on
 * something other than the number - `maxLabelledNumber` is the same ceiling with
 * that line paid for. Neither ceiling reaches a full-length order reference.
 *
 * Ready tiles stay a step above preparing at every count - the column customers
 * act on has to read as the loud one.
 */
export const boardTileTrack = (count, isReady) => {
  if (isReady) {
    if (count <= 3) {
      return {
        minTrack: "clamp(12rem, 21vw, 26rem)",
        minRow: "clamp(7rem, 20vh, 15rem)",
        maxNumber: "30vh",
        maxLabelledNumber: "28vh",
      };
    }
    if (count <= 8) {
      return {
        minTrack: "clamp(10rem, 15vw, 18rem)",
        minRow: "clamp(5rem, 14vh, 10rem)",
        maxNumber: "15vh",
        maxLabelledNumber: "13.5vh",
      };
    }
    if (count <= 20) {
      return {
        minTrack: "clamp(8rem, 13vw, 16rem)",
        minRow: "clamp(3.5rem, 9vh, 7rem)",
        maxNumber: "6.8vh",
        maxLabelledNumber: "4.9vh",
      };
    }
    return {
      minTrack: "clamp(6.5rem, 10.5vw, 12rem)",
      minRow: "clamp(3.25rem, 7.5vh, 6rem)",
      maxNumber: "5.4vh",
      maxLabelledNumber: "3.2vh",
    };
  }

  if (count <= 6) {
    return {
      minTrack: "clamp(6.5rem, 11vw, 12rem)",
      minRow: "clamp(4rem, 10vh, 7rem)",
      maxNumber: "8vh",
      maxLabelledNumber: "7vh",
    };
  }
  return {
    minTrack: "clamp(6rem, 10vw, 11rem)",
    minRow: "clamp(3rem, 7vh, 5.5rem)",
    maxNumber: "4.9vh",
    maxLabelledNumber: "3vh",
  };
};

/**
 * The share of a tile's width a number may occupy. The remainder is the tile's
 * padding plus enough air that a wide glyph never reaches the edge - the tile
 * clips rather than wraps, and a clipped order number is a customer who never
 * gets called.
 */
export const NUMBER_WIDTH_BUDGET_CQI = 84;

/**
 * Per-glyph advance for the uppercase alphanumeric set an order reference is
 * drawn from, as a share of the font size. Measured, not guessed: "KKWA" - four
 * of the widest capitals a reference can hold - renders 171px wide at 55px on
 * the board itself. Pager numbers are digits and so narrower; budgeting them at
 * this width simply leaves more air.
 */
export const GLYPH_WIDTH_EM = 0.78;

/**
 * Font size for a called number, as a share of its own tile's width.
 *
 * A pager "7" and an order reference "KKWA" land in identically sized tiles, so
 * a single size leaves one of them looking lost. Scaling by length lets both
 * fill their tile, which is what makes them readable from across a room.
 */
export const boardNumberScale = (ref) => {
  const length = String(ref || "").length;
  if (length <= 1) {
    return "46cqi";
  }
  if (length === 2) {
    return "40cqi";
  }
  if (length === 3) {
    return "32cqi";
  }
  if (length === 4) {
    return "25cqi";
  }
  // Longer than any order reference, which is always four glyphs - so this is
  // an unusually long pager number. Sized straight from the budget rather than
  // by eye, so it cannot outgrow its tile however long it gets.
  return `${Math.floor(NUMBER_WIDTH_BUDGET_CQI / (length * GLYPH_WIDTH_EM))}cqi`;
};
